import React, { useState, useEffect } from 'react';
import { Row, Col, Modal, ModalBody, Label, CustomInput } from 'reactstrap';
import Select from 'react-select';
import { BrowserView, MobileView } from 'react-device-detect';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Translate, { t } from '../common/Translate';
import CustomCost from './CustomCost';
import useValue from '../../services/Form/useValue';

const ModalListSelect = (props) => {
  const { isOpen } = props;

  return (
    <>
      <Modal isOpen={isOpen} centered>
        <ModalBody className="modal-body">
          <ListSelect {...props} />
        </ModalBody>
      </Modal>
    </>
  );
};

ModalListSelect.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default ModalListSelect;

// Select element in the list
const ListSelect = (props) => {
  const { questionId, listsOption: children, selectedElement } = props;
  const [elementSelected, setElementSelected] = useState(selectedElement);
  const [options, setOptions] = useState([]);
  const [showSelectItem, setShowSelectItem] = useState(true);

  const handleChangeElement = (e) => {
    const element = e.value;
    if (showSelectItem) setShowSelectItem(false);
    if (element === '') setElementSelected('');
    else setElementSelected(e.value);
  };

  if (children && options.length === 0) {
    const d = [];
    Object.keys(children).forEach((i) => {
      d.push({ label: t(i), value: i });
    });
    setOptions(d);
  }

  return (
    <div data-cy={questionId}>
      <Label className="pl-2 text-left">
        <Translate code="damaged_element" />
      </Label>
      <BrowserView>
        <Select
          id="element"
          className="rounded"
          options={options}
          placeholder={t('select_element')}
          onChange={(e) => handleChangeElement(e)}
          data-cy="element"
          value={elementSelected && { label: t(elementSelected), value: elementSelected }}
          isSearchable={false}
        />
      </BrowserView>
      <MobileView>
        <CustomInput
          id="element"
          className="text-left form-control"
          type="select"
          onChange={(e) => {
            handleChangeElement(e.target);
          }}
          value={elementSelected}
          data-cy="element"
        >
          {showSelectItem && (
            <option value="" hidden>
              {t('select_element')}
            </option>
          )}
          {children !== undefined &&
            children !== null &&
            Object.keys(children).map((childId) => (
              <option key={childId} value={childId}>
                {t(childId)}
              </option>
            ))}
        </CustomInput>
      </MobileView>
      <ListDamages {...props} elementSelected={elementSelected} />
    </div>
  );
};

ListSelect.propTypes = {
  questionId: PropTypes.string.isRequired,
  listsOption: PropTypes.shape(),
  selectedElement: PropTypes.string,
};

ListSelect.defaultProps = {
  listsOption: null,
  selectedElement: null,
};

// Select the damage in the list or put it manually
const ListDamages = (props) => {
  const [customDamage, setCustomDamage] = useState(false);
  const [modalButtonDisable, setModalButtonDisable] = useState(true);
  const { formId, sectionId, questionId, elementSelected, listsOption: children, closeModal } = props;
  const question = _.get(children, elementSelected, '');
  const questionChild = _.get(children, `${elementSelected}.id`, '');
  const questionChildType = _.get(children, `${elementSelected}.type`, '');

  const [value, setValue] = useValue(
    formId,
    sectionId,
    questionId,
    question.type,
    `children.${elementSelected}.value`,
    '',
    questionChild,
    questionChildType,
  );

  // keep the value in state waiting for validation
  const [tempValue, setTempValue] = useState(value);
  const [cost, setCost] = useState('');
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (value !== null) {
      setLabel(value?.[tempValue]?.label);
      setCost(value?.[tempValue]?.cost);
      if (value && typeof value !== 'object' && !tempValue) setTempValue(value);
    } else {
      setLabel('');
      setCost('');
      setTempValue('');
      setCustomDamage(false);
    }
  }, [elementSelected, value]);

  useEffect(() => {
    if (customDamage && cost && label) {
      setModalButtonDisable(false);
    } else if (!customDamage && tempValue) {
      setModalButtonDisable(false);
    } else {
      setModalButtonDisable(true);
    }
  }, [customDamage, cost, label, tempValue]);

  const handleChangeDamage = (e) => {
    let damage = e.value;
    const custom_costs = _.get(question, `custom_cost`, []);
    if (custom_costs.includes(damage)) {
      setTempValue(damage);
      setCustomDamage(true);
      return;
    }
    if (damage !== '') {
      setModalButtonDisable(false);
    } else {
      setModalButtonDisable(true);
      damage = null;
    }
    if (customDamage) setCustomDamage(false);
    setTempValue(damage);
  };

  // when clic on validate, the data are added into the indexed DB
  const validateValue = async () => {
    if (customDamage) {
      await setValue({
        [tempValue]: {
          cost,
          label,
        },
      });
    } else await setValue(tempValue);
    closeModal();
  };

  let damagesBrowser = [];
  let damagesMobile = null;
  if (question !== undefined) {
    const { options } = question;
    if (!options || options === undefined || !Array.isArray(options)) {
      damagesBrowser = [];
      damagesMobile = null;
    } else if (options.length === 1) {
      damagesBrowser.push({ label: t(options[0]), value: options[0] });
      damagesMobile = (
        <option key={options[0]} value={options[0]}>
          {t(options[0])}
        </option>
      );
      if (tempValue !== options[0]) {
        handleChangeDamage({ value: options[0] }); // automatically set the value if only 1 choice
        setTempValue(options[0]);
      }
    } else {
      damagesBrowser = [{ label: t('select_damage'), value: '' }];
      options.map((option) => damagesBrowser.push({ label: t(option), value: option }));
      damagesMobile = (
        <>
          <option value="">{t('select_damage')}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {t(option)}
            </option>
          ))}
        </>
      );
      if (value && typeof value === 'object' && !tempValue && elementSelected) {
        Object.keys(value).forEach((i) => setTempValue(i));
        setCustomDamage(true);
      }
    }
  }

  const checkSingleOption = () => {
    const { options } = question;
    if (options && options.length === 1 && ['other', 'damage'].includes(options[0])) return true;
    return false;
  };

  return (
    <>
      {!(elementSelected === undefined || !elementSelected) && (
        <div className="form-group mt-4" data-cy={questionId}>
          {!checkSingleOption() && (
            <>
              <Label className="pl-2 text-left">
                <Translate code="Dommage" />
              </Label>
              <BrowserView>
                <Select
                  id="damage"
                  className="rounded"
                  value={{ value: tempValue, label: t(tempValue) }}
                  options={damagesBrowser}
                  placeholder={t('select_element')}
                  isDisabled={elementSelected === undefined || !elementSelected}
                  onChange={(e) => handleChangeDamage(e)}
                  data-cy="damages"
                />
              </BrowserView>
              <MobileView>
                <CustomInput
                  id="damage"
                  className="text-left form-control"
                  type="select"
                  disabled={elementSelected === undefined || !elementSelected}
                  value={tempValue}
                  onChange={(e) => {
                    handleChangeDamage(e.target);
                  }}
                  data-cy="damages"
                >
                  {damagesMobile}
                </CustomInput>
              </MobileView>
            </>
          )}
          {customDamage && (
            <CustomCost questionId={questionId} label={label} cost={cost} setLabel={setLabel} setCost={setCost} />
          )}
        </div>
      )}
      <Row className="mt-4 mb-2">
        <Col xs="5" className="text-center" data-cy={questionId}>
          <button type="button" className="btn btn-link px-2" onClick={closeModal} data-cy="model-cancel-btn">
            <Translate code="cancel" />
          </button>
        </Col>
        <Col xs="7" className="text-center" data-cy="validateDamage">
          <button
            type="submit"
            className={`btn ${modalButtonDisable ? 'btn-4' : 'btn-1'}`}
            disabled={modalButtonDisable}
            onClick={validateValue}
          >
            <Translate code="validate" />
          </button>
        </Col>
      </Row>
    </>
  );
};

ListDamages.propTypes = {
  formId: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired,
  elementSelected: PropTypes.string.isRequired,
  listsOption: PropTypes.shape(),
  closeModal: PropTypes.func.isRequired,
};

ListDamages.defaultProps = {
  listsOption: null,
};
